import React from 'react';
import {
  Layout, Button, Space, Card, Row, Col, Tag,
  Typography, DatePicker, InputNumber, theme, Collapse,
  Menu
} from 'antd';
import {
  UserOutlined,
  SearchOutlined,
  WifiOutlined,
  CoffeeOutlined,
  CarOutlined,
  StarFilled
} from '@ant-design/icons';

const { Header, Content, Footer } = Layout;
const { Title, Text, Paragraph } = Typography;
const { RangePicker } = DatePicker;

// Component แยกสำหรับหน้า Landing Page (จองโรงแรม)
const PreviewLanding = ({ viewMode }: { viewMode: string }) => {
  const { token } = theme.useToken();
  const isMobile = viewMode === 'mobile';

  // Mock Data ห้องพัก (ปรับโครงสร้างให้เหมือน HotelRoomOverview)
  const rooms = [
    {
        id: 1,
        name: "Deluxe Ocean View",
        short: "Experience ultimate relaxation with a stunning view of the ocean. Perfect for couples.",
        price: "3,500",
        img: "https://images.unsplash.com/photo-1611892440504-42a792e24d32?auto=format&fit=crop&q=80&w=300&h=200",
        highlights: ["Sea View", "King Bed", "Balcony"],
        amenities: ["Breakfast", "Free Wifi", "Mini Bar"]
    },
    {
        id: 2,
        name: "Family Suite",
        short: "Spacious suite designed for families, featuring a separate living area and kitchen.",
        price: "5,200",
        img: "https://images.unsplash.com/photo-1582719478250-c89cae4dc85b?auto=format&fit=crop&q=80&w=300&h=200",
        highlights: ["2 Bedrooms", "Kitchen", "City View"],
        amenities: ["Free Wifi", "Smart TV", "Bathtub", "Kitchenette"]
    }
  ];

  return (
    <Layout style={{ height: '100%', overflow: 'hidden' }}>
       {/* Header */}
       <Header style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: isMobile ? '0 16px' : '0 24px',
            height: isMobile ? 56 : 64,
            borderBottom: `1px solid ${token.colorBorderSecondary}`,
            marginTop: isMobile ? 20 : 0,
            zIndex: 10,
            transition: 'all 0.3s'
        }}>
               <Menu
                  mode="horizontal" defaultSelectedKeys={['1']}
                  items={['Home','menu1','menu2','menu3'].map((key, index) => ({ key: String(index + 1), label: key }))}
                  style={{ flex: 1, borderBottom: 'none', background: 'transparent' }}
                />
       </Header>

       <Content style={{ overflowY: 'auto', height: '100%', scrollbarWidth: 'none', position: 'relative' }}>
            {/* Hero Section */}
            <div style={{
                height: isMobile ? 200 : 350,
                backgroundImage: 'url("https://fromas.sgp1.cdn.digitaloceanspaces.com/sample/hotel_images/h1.jpg")',
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                position: 'relative',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
            }}>
                <div style={{
                    position: 'absolute', inset: 0,
                    background: 'linear-gradient(to bottom, rgba(0,0,0,0.2), rgba(0,0,0,0.5))'
                }} />
                <Title level={isMobile ? 3 : 1} style={{ color: '#fff', position: 'relative', zIndex: 1, textShadow: '0 2px 4px rgba(0,0,0,0.5)', margin: 0 }}>
                    Relax in Paradise
                </Title>
            </div>

            {/* ✅ Search Widget (Sticky Top) */}
            <div style={{ 
                padding: '0 24px', 
                marginTop: -40, 
                position: 'sticky', // ทำให้ติดด้านบน
                top: 0,             // ติดที่ขอบบนสุด
                zIndex: 100,        // ลอยเหนือเนื้อหาอื่น
                pointerEvents: 'none' // ให้คลิกทะลุพื้นที่ว่างข้างๆ ได้ (ถ้ามี)
            }}>
                <Card
                    variant='borderless'
                    style={{ 
                        boxShadow: '0 4px 12px rgba(0,0,0,0.1)', 
                        borderRadius: token.borderRadiusLG,
                        pointerEvents: 'auto' // ให้คลิกที่ Card ได้
                    }}
                    bodyStyle={{ padding: isMobile ? 16 : 24 }}
                >
                    <Title level={5} style={{ marginTop: 0, marginBottom: 16 }}>Find your room</Title>
                    <Row gutter={[16, 16]}>
                        <Col span={isMobile ? 24 : 10}>
                            <RangePicker style={{ width: '100%' }} />
                        </Col>
                        <Col span={isMobile ? 12 : 5}>
                            <InputNumber min={1} max={10} defaultValue={2} addonBefore={<UserOutlined />} style={{ width: '100%' }} />
                        </Col>
                        <Col span={isMobile ? 12 : 5}>
                            <InputNumber min={0} max={5} defaultValue={0} addonBefore="Child" style={{ width: '100%' }} />
                        </Col>
                        <Col span={isMobile ? 24 : 4}>
                            <Button type="primary" block icon={<SearchOutlined />} style={{height: '100%', boxShadow: 'none'}}>Check</Button>
                        </Col>
                    </Row>
                </Card>
            </div>

            {/* Content Section */}
            <div style={{ padding: isMobile ? 16 : 32, maxWidth: 960, margin: '0 auto' }}>
                <div style={{ marginBottom: 32 }}>
                    <Title level={2}>Welcome to Hotel Soft</Title>
                    <Paragraph type="secondary">
                        Experience luxury and comfort in the heart of the city. Our hotel offers convenient access to major attractions and premium amenities.
                    </Paragraph>
                    <Space size={[0, 8]} wrap>
                        <Tag icon={<WifiOutlined />}>Free Wi-Fi</Tag>
                        <Tag icon={<CoffeeOutlined />}>Breakfast</Tag>
                        <Tag icon={<CarOutlined />}>Parking</Tag>
                        <Tag><StarFilled /> 5 Stars</Tag>
                    </Space>
                </div>

                <Title level={4}>Available Rooms</Title>
                
                {/* ✅ Available Rooms (HotelRoomOverview Style) */}
                <Space orientation="vertical" size={16} style={{ width: "100%" }}>
                    {rooms.map((r) => (
                        <Card
                            key={r.id}
                            style={{ borderRadius: 16, overflow: "hidden" }}
                            bodyStyle={{ padding: isMobile ? 0 : 16 }}
                            hoverable
                        >
                            {/* Logic การแสดงผลแยก Mobile/Desktop */}
                            {isMobile ? (
                                // 📱 Mobile Layout: รูปบน / เนื้อหาล่าง
                                <div>
                                    <div style={{
                                        height: 200, width: '100%',
                                        backgroundImage: `url(${r.img})`,
                                        backgroundSize: 'cover', backgroundPosition: 'center'
                                    }} />
                                    <div style={{ padding: 16 }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                                            <Title level={5} style={{ margin: 0 }}>{r.name}</Title>
                                        </div>
                                        <Paragraph type="secondary" style={{ fontSize: "0.95rem" }}>{r.short}</Paragraph>
                                        
                                        {/* Highlights */}
                                        <div style={{ marginBottom: 8 }}>
                                            <Text strong>Highlights: </Text>
                                            <Space size={[4, 4]} wrap>
                                                {r.highlights.map(h => <Tag key={h} variant='filled'>{h}</Tag>)}
                                            </Space>
                                        </div>

                                        {/* Amenities (Collapse) */}
                                        <Collapse ghost size="small" items={[{
                                            key: 'amenities', label: 'Facilities',
                                            children: <Space wrap>{r.amenities.map(a => <Tag key={a}>{a}</Tag>)}</Space>
                                        }]} style={{ marginLeft: -8, marginRight: -8 }} />
                                    </div>
                                </div>
                            ) : (
                                // 🖥 Desktop Layout: รูปซ้าย / เนื้อหาขวา (Grid)
                                <div style={{ display: "grid", gridTemplateColumns: "1fr 2fr", gap: 16, alignItems: "start" }}>
                                    <div style={{
                                        height: '100%', minHeight: 180, borderRadius: 8,
                                        backgroundImage: `url(${r.img})`,
                                        backgroundSize: 'cover', backgroundPosition: 'center'
                                    }} />
                                    
                                    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                                        <div style={{ flex: 1 }}>
                                            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                                <Title level={4} style={{ margin: 0 }}>{r.name}</Title>
                                            </div>
                                            <Paragraph type="secondary" style={{ margin: "8px 0" }}>{r.short}</Paragraph>

                                            <div style={{ marginBottom: 8 }}>
                                                <Text strong>Highlights: </Text>
                                                <Space size={[4, 4]} wrap>
                                                    {r.highlights.map(h => <Tag key={h}>{h}</Tag>)}
                                                </Space>
                                            </div>
                                            <div>
                                                <Text strong>Facilities: </Text>
                                                <Space size={[4, 4]} wrap>
                                                    {r.amenities.map(a => <Tag key={a}>{a}</Tag>)}
                                                </Space>
                                            </div>
                                        </div>
                                      
                                    </div>
                                </div>
                            )}
                        </Card>
                    ))}
                </Space>
            </div>
       </Content>
    </Layout>
  );
};

export default PreviewLanding;